import apiV2 from '../api/v2';

export { ruleBreakdown, rulesForVersion } from './productPricingRules.ts';

export type DiscountType = 'flat' | 'percentage';

export type ResolutionReason = 'resolved' | 'no_match' | 'ambiguous';

// A chargeable version of a base product (only versions with a Bill.com item
// are returned here). `selectors` are the matching criteria the backend
// resolved against this practice; `label` is display-only.
export interface PricedVersion {
    id: string;
    label: string;
    selectors: Record<string, string>;
    priceCents: number;
    billId: string | null;
}

// A live, billable version whose selectors all match this practice.
export interface MatchingVersion {
    id: string;
    label: string;
    priceCents: number;
    effectivePriceCents: number | null;
    isDefault: boolean;
}

// An ACTIVE discount rule this practice is eligible for. Version-scoped
// rules only apply when their version is the one charged.
export interface PricedRule {
    id: string;
    name: string;
    discountType: DiscountType;
    discountValue: number;
    appliesToVersionId: string | null;
}

// One row per chargeable base product, priced for one practice. When no
// version resolves (resolutionReason 'no_match' / 'ambiguous') the prices are
// null and the admin must pick from `versions` at charge time.
export interface PricedProduct {
    id: string;
    name: string;
    category: string | null;
    subCategory: string | null;
    versions: PricedVersion[];
    resolvedVersionId: string | null;
    resolutionReason: ResolutionReason;
    // Resolved version's list price; null when unresolved.
    defaultPriceCents: number | null;
    rules: PricedRule[];
    // Resolved version's price after its applicable rules; null when unresolved.
    effectivePriceCents: number | null;
    // Times this product (any version) has been charged to the practice.
    chargedCount: number;
    // Ordered by label; more than one means staff can pick the version to price from.
    matchingVersions: MatchingVersion[];
}

export const TREATMENT_CATEGORY = 'Treatment';

// Mirrors the backend's applyRules: rules STACK - percentages compound
// first, then flat amounts subtract, floored at $0.
export function applyRules(priceCents: number, rules: PricedRule[]): number {
    let price = priceCents;
    for (const rule of rules) {
        if (rule.discountType === 'percentage') {
            price = Math.round(price * (1 - rule.discountValue / 100));
        }
    }
    for (const rule of rules) {
        if (rule.discountType === 'flat') {
            price -= Math.round(rule.discountValue * 100);
        }
    }
    return Math.max(0, price);
}

export async function fetchPracticeProductPricing(practiceId: string): Promise<PricedProduct[]> {
    const response = await apiV2.get<{ success: boolean; data: PricedProduct[] }>(
        `/practices/${practiceId}/product-pricing`
    );
    return response.data.data;
}
